import React, { useState } from 'react';
import { useProject } from '../../hooks/useProject';
import { useFormState } from '../../hooks/useFormState';
import { AlertBlock } from '../../components/AlertBlock';
import { SubmitButton } from '../../components/SubmitButton';
import { api } from '../../services/api';
import { notifySuccess } from '../../services/notify';

interface RemoteCollectionForm extends Record<string, unknown> {
  custodianName: string;
  custodianEmailAddress: string;
  custodianPhysicalAddress: string;
}

const EMPTY: RemoteCollectionForm = {
  custodianName: '',
  custodianEmailAddress: '',
  custodianPhysicalAddress: '',
};

export function RemoteCollectionTab({ projectId }: { projectId: string }) {
  const { data: project } = useProject(projectId);
  const form = useFormState<RemoteCollectionForm>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  const fieldError = (field: keyof RemoteCollectionForm) =>
    form.touched[field] && !String(form.values[field] ?? '').trim();

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.values.custodianEmailAddress);
  const isValid =
    form.values.custodianName.trim() !== '' &&
    form.values.custodianEmailAddress.trim() !== '' &&
    isEmailValid &&
    form.values.custodianPhysicalAddress.trim() !== '';
  const canSubmit = form.dirty && isValid && !saving;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    form.touchAll();
    if (!isValid) return;

    setSaving(true);
    setError(false);
    try {
      await api.post(`/project/${projectId}/remoteCollection`, form.values);
      notifySuccess({
        title: 'Remote Collection Successfully Requested',
        description: 'We will be in contact shortly with the details',
        timeout: 5000,
      });
      form.reset(EMPTY);
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2><i className="fas fa-info" aria-hidden="true" /> Remote Collection</h2>

      {error && (
        <AlertBlock type="danger">
          Failed to submit remote collection request. Please try again.
        </AlertBlock>
      )}

      <form id="RemoteCollectionForm" className="form-horizontal" onSubmit={handleSubmit} noValidate>

        <div className="form-group">
          <label className="control-label col-sm-2">Project</label>
          <div className="col-sm-7">
            <p className="form-control-static">{project?.name}</p>
          </div>
        </div>

        <div className={`form-group${fieldError('custodianName') ? ' has-error' : ''}`}>
          <label className="control-label col-sm-2" htmlFor="custodianName">Custodian Name</label>
          <div className="col-sm-7">
            {fieldError('custodianName') && (
              <span className="help-block">Name is required</span>
            )}
            <input
              id="custodianName"
              data-testid="admin-remote-name-input"
              type="text"
              className="form-control"
              value={form.values.custodianName}
              maxLength={255}
              required
              disabled={saving}
              onChange={e => form.set('custodianName', e.target.value)}
              onBlur={() => form.blur('custodianName')}
            />
          </div>
        </div>

        <div className={`form-group${fieldError('custodianEmailAddress') ? ' has-error' : ''}`}>
          <label className="control-label col-sm-2" htmlFor="custodianEmailAddress">Custodian Email Address</label>
          <div className="col-sm-7">
            {fieldError('custodianEmailAddress') && (
              <span className="help-block">Email address is required</span>
            )}
            <input
              id="custodianEmailAddress"
              data-testid="admin-remote-email-input"
              type="email"
              className="form-control"
              value={form.values.custodianEmailAddress}
              required
              disabled={saving}
              onChange={e => form.set('custodianEmailAddress', e.target.value)}
              onBlur={() => form.blur('custodianEmailAddress')}
            />
          </div>
        </div>

        <div className={`form-group${fieldError('custodianPhysicalAddress') ? ' has-error' : ''}`}>
          <label className="control-label col-sm-2" htmlFor="custodianPhysicalAddress">Custodian Physical Address</label>
          <div className="col-sm-7">
            {fieldError('custodianPhysicalAddress') && (
              <span className="help-block">Address is required</span>
            )}
            <input
              id="custodianPhysicalAddress"
              data-testid="admin-remote-address-input"
              type="text"
              className="form-control"
              value={form.values.custodianPhysicalAddress}
              maxLength={255}
              required
              disabled={saving}
              onChange={e => form.set('custodianPhysicalAddress', e.target.value)}
              onBlur={() => form.blur('custodianPhysicalAddress')}
            />
          </div>
        </div>

        <div className="form-group">
          <div className="col-sm-6 col-sm-offset-2">
            <SubmitButton
              pending={saving}
              pendingLabel="requesting..."
              data-testid="admin-remote-request-btn"
              disabled={!canSubmit}
            >Request</SubmitButton>
          </div>
        </div>

      </form>
    </div>
  );
}
